// ItemInput — type-switcher voor één schouw-item.
//
// Ondersteunde item.type-waarden (uit clusters.json):
//   text / textarea / number / select / boolean / photo /
//   dimensions / range / contact / date / datetime / file /
//   computed / ref
//
// Alle types hergebruiken MUI-defaults; grote tap-targets via theme.
// Soft-warning onder het item via ValidationDisplay.

import React, { useCallback, useMemo, useRef } from 'react';
import {
    Autocomplete,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    OutlinedInput,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
    Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DimensionsInput from './DimensionsInput.jsx';
import ContactInput from './ContactInput.jsx';
import RangeInput from './RangeInput.jsx';
import ItemPhotos from './ItemPhotos.jsx';
import KvkSearchInput from './KvkSearchInput.jsx';
import ValidationDisplay from './ValidationDisplay.jsx';

/** Simpele validator op basis van item.validation. */
function validate(item, value) {
    if (value === null || value === undefined || value === '') { return null; }
    const v = item.validation;
    if (!v) { return null; }
    const num = Number(value);
    if (typeof v.max === 'number' && !Number.isNaN(num) && num > v.max) {
        return { severity: v.severity || 'soft_warning', message: v.message, item };
    }
    if (typeof v.min === 'number' && !Number.isNaN(num) && num < v.min) {
        return { severity: v.severity || 'soft_warning', message: v.message, item };
    }
    return null;
}

/** Patroon-check voor text-velden (bijv. KvK, EAN). */
function patternError(item, value) {
    if (!item.pattern || !value) { return null; }
    try {
        const re = new RegExp(item.pattern);
        if (!re.test(String(value))) {
            return `Verwachte opmaak: ${item.pattern}`;
        }
    } catch { /* ignore malformed */ }
    return null;
}

export default function ItemInput({
    item,
    value,
    onChange,
    projectId,
    disabled,
    onAddToRegister,
    inRegister,
    allSurveyData,
    onFillSiblings,
    uploadItemId,
}) {
    const uploadId = uploadItemId || item.id;
    const { t } = useTranslation();

    const warning = useMemo(() => validate(item, value), [item, value]);
    const patErr = useMemo(() => patternError(item, value), [item, value]);

    // showIf — eenvoudige evaluator ("B3.5==true"). We ondersteunen alleen
    // ==/!= tegen literals; onbekende refs → true (zichtbaar).
    const hidden = useMemo(() => {
        if (!item.showIf) { return false; }
        const m = item.showIf.match(/^([A-Z]\d+(?:\.\d+)?)(==|!=)(.+)$/);
        if (!m) { return false; }
        const [, ref, op, raw] = m;
        const literal = raw.trim();
        const target = literal === 'true' ? true : literal === 'false' ? false
            : /^-?\d+(\.\d+)?$/.test(literal) ? Number(literal)
                : literal.replace(/^["']|["']$/g, '');
        // Zoek de ref in alle surveyData (clusterA → sectionA → itemID).
        let found;
        for (const cluster of Object.values(allSurveyData || {})) {
            for (const section of Object.values(cluster || {})) {
                if (section && Object.prototype.hasOwnProperty.call(section, ref)) {
                    found = section[ref];
                }
            }
        }
        return op === '==' ? !(found === target) : (found === target);
    }, [item.showIf, allSurveyData]);

    // Upload-items bewaren zelf geen waarde; een marker houdt de
    // verplicht-controle bij zonder een bestaande notitie te overschrijven.
    const valueRef = useRef(value);
    valueRef.current = value;
    const syncUploadMarker = useCallback((count) => {
        const v = valueRef.current;
        const isMarker = v && typeof v === 'object' && v.uploaded === true;
        const legacy = Array.isArray(v) || (v && typeof v === 'object' && (v.url || v.status === 'local'));
        if (count > 0 && (!v || legacy)) { onChange({ uploaded: true }); }
        else if (count === 0 && (isMarker || (legacy && !v.name))) { onChange(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (hidden) { return null; }

    const label = (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {item.label}
                {item.unit && <span style={{ color: '#777', fontWeight: 400 }}> ({item.unit})</span>}
            </Typography>
            {/* Tijdens een testperiode (REQUIRED_OFF_UNTIL op de server) geen labels. */}
            {item.required_suspended ? null : item.required ? (
                <Chip size="small" color="primary" variant="outlined" label={t('common.required')} />
            ) : (
                <Chip size="small" variant="outlined" label={t('common.optional')} />
            )}
            {item.id && (
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                    {item.id}
                </Typography>
            )}
        </Stack>
    );

    const help = item.help ? (
        <FormHelperText sx={{ mt: 0.5 }}>{item.help}</FormHelperText>
    ) : null;

    let input;
    switch (item.type) {
        case 'text':
            input = (
                <TextField
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    error={Boolean(patErr)}
                    helperText={patErr || undefined}
                    disabled={disabled}
                    inputProps={item.pattern ? { pattern: item.pattern } : undefined}
                />
            );
            break;
        case 'textarea':
            input = (
                <TextField
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    multiline
                    minRows={3}
                    disabled={disabled}
                />
            );
            break;
        case 'number':
            input = (
                <TextField
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    type="number"
                    inputMode="decimal"
                    inputProps={{
                        min: item.min,
                        max: item.max,
                        step: item.precision ? `${1 / Math.pow(10, item.precision)}` : 'any',
                    }}
                    disabled={disabled}
                />
            );
            break;
        case 'select': {
            if (item.multiselect) {
                const selected = Array.isArray(value) ? value : [];
                input = (
                    <FormControl>
                        <Select
                            multiple
                            displayEmpty
                            value={selected}
                            input={<OutlinedInput />}
                            onChange={(e) => {
                                const v = e.target.value;
                                onChange(typeof v === 'string' ? v.split(',') : v);
                            }}
                            renderValue={(sel) => (sel.length === 0 ? <em>Kies een of meer</em> : (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {sel.map(x => <Chip key={x} label={x} size="small" />)}
                                </Stack>
                            ))}
                            disabled={disabled}
                        >
                            {(item.options || []).map(opt => (
                                <MenuItem key={opt} value={opt}>
                                    <Checkbox checked={selected.indexOf(opt) > -1} />
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            } else {
                input = (
                    <FormControl>
                        <Select
                            value={value || ''}
                            onChange={(e) => onChange(e.target.value)}
                            displayEmpty
                            disabled={disabled}
                        >
                            <MenuItem value=""><em>Geen keuze</em></MenuItem>
                            {(item.options || []).map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            }
            break;
        }
        case 'kvk_search': {
            input = (
                <KvkSearchInput
                    value={value || ''}
                    onChange={onChange}
                    onFillSiblings={onFillSiblings}
                    disabled={disabled}
                />
            );
            break;
        }
        case 'autocomplete': {
            input = (
                <Autocomplete
                    freeSolo={item.allow_custom !== false}
                    options={item.options || []}
                    value={value || ''}
                    onChange={(_, newVal) => onChange(newVal || '')}
                    onInputChange={(_, newVal, reason) => {
                        if (reason === 'input' && item.allow_custom !== false) {
                            onChange(newVal);
                        }
                    }}
                    disabled={disabled}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Begin te typen of kies uit lijst"
                        />
                    )}
                    autoHighlight
                    selectOnFocus
                    clearOnBlur={false}
                    handleHomeEndKeys
                />
            );
            break;
        }
        case 'boolean':
            input = (
                <RadioGroup
                    row
                    value={value === true ? 'yes' : value === false ? 'no' : ''}
                    onChange={(e) => onChange(e.target.value === 'yes')}
                >
                    <FormControlLabel value="yes" control={<Radio />} label={t('common.yes')} disabled={disabled} />
                    <FormControlLabel value="no" control={<Radio />} label={t('common.no')} disabled={disabled} />
                </RadioGroup>
            );
            break;
        case 'photo':
            input = (
                <ItemPhotos
                    projectId={projectId}
                    itemId={uploadId}
                    allowMultiple={Boolean(item.multiple)}
                    prominent
                    disabled={disabled}
                    onCountChange={syncUploadMarker}
                />
            );
            break;
        case 'dimensions':
            input = (
                <DimensionsInput
                    value={value}
                    onChange={onChange}
                    unit={item.unit}
                    itemLabel={item.label}
                    disabled={disabled}
                />
            );
            break;
        case 'range':
            input = (
                <RangeInput
                    value={value}
                    onChange={onChange}
                    unit={item.unit}
                    disabled={disabled}
                />
            );
            break;
        case 'contact':
            input = (
                <ContactInput value={value} onChange={onChange} disabled={disabled} />
            );
            break;
        case 'date':
            input = (
                <TextField
                    type="date"
                    value={typeof value === 'string' ? value.slice(0, 10) : ''}
                    onChange={(e) => onChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                />
            );
            break;
        case 'datetime':
            input = (
                <TextField
                    type="datetime-local"
                    value={typeof value === 'string' ? value.slice(0, 16) : ''}
                    onChange={(e) => onChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                />
            );
            break;
        case 'file': {
            const note = value && typeof value === 'object' ? (value.ref || null) : null;
            const neverUploaded = value && typeof value === 'object' && value.status === 'local';
            input = (
                <Stack spacing={1} alignItems="flex-start">
                    {note && (
                        <Typography variant="body2" color="text.secondary">{note}</Typography>
                    )}
                    {neverUploaded && (
                        <Typography variant="body2" color="warning.dark">
                            "{value.name}" is eerder gekozen maar niet geupload. Upload het bestand opnieuw.
                        </Typography>
                    )}
                    <ItemPhotos
                        projectId={projectId}
                        itemId={uploadId}
                        mode="file"
                        accept={item.accept}
                        disabled={disabled}
                        onCountChange={syncUploadMarker}
                    />
                </Stack>
            );
            break;
        }
        case 'computed': {
            // Afgeleide waarde — enkel display.
            let display = '';
            try {
                if (item.formula && allSurveyData) {
                    const refs = [...new Set(item.formula.match(/[A-Z]\d+(\.\d+)?/g) || [])].sort((x, y) => y.length - x.length);
                    let expr = item.formula;
                    for (const r of refs) {
                        let v;
                        for (const cl of Object.values(allSurveyData)) {
                            for (const sec of Object.values(cl || {})) {
                                if (sec && Object.prototype.hasOwnProperty.call(sec, r)) {
                                    v = sec[r];
                                }
                            }
                        }
                        expr = expr.replace(new RegExp(`${r.replace('.', '\\.')}(?![\\d.])`, 'g'), Number(v) || 0);
                    }
                    display = Function(`"use strict"; return (${expr})`)();
                }
            } catch { display = '—'; }
            input = (
                <TextField
                    value={display}
                    InputProps={{ readOnly: true }}
                    disabled
                />
            );
            break;
        }
        case 'ref': {
            // Lookup optie-lijst over cluster/field — showt dropdown.
            const opts = [];
            if (allSurveyData && item.ref_cluster) {
                const cl = allSurveyData[item.ref_cluster] || {};
                for (const sec of Object.values(cl)) {
                    if (sec && Object.prototype.hasOwnProperty.call(sec, item.ref_field)) {
                        const v = sec[item.ref_field];
                        if (v && !opts.includes(v)) { opts.push(v); }
                    }
                }
            }
            if (value && !opts.includes(value)) { opts.push(value); }
            input = (
                <FormControl>
                    <Select
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        displayEmpty
                        disabled={disabled}
                    >
                        <MenuItem value=""><em>Geen keuze</em></MenuItem>
                        {opts.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
            break;
        }
        default:
            input = (
                <TextField
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                />
            );
    }

    // Inline foto-bijlagen alleen bij types waar visuele context waarde heeft.
    // Puur-tekstuele/structurele data (naam, nummer, KvK, adres, keuzes,
    // datums) krijgen GEEN foto-knop — die zouden de UI onnodig vol maken.
    //
    // Default-aan: textarea (vrije notities), dimensions (ruimte), range.
    // Expliciete opt-in per item: `allow_photos: true` → altijd tonen.
    // Expliciete opt-out: `allow_photos: false` → altijd verbergen.
    const PHOTO_FRIENDLY_TYPES = new Set(['textarea', 'dimensions', 'range']);
    const explicitOn = item.allow_photos === true;
    const explicitOff = item.allow_photos === false;
    const showItemPhotos = projectId
        && item.id
        && !explicitOff
        && (explicitOn || PHOTO_FRIENDLY_TYPES.has(item.type));

    return (
        <Box id={`item-${item.id}`} sx={{ py: 1.5, scrollMarginTop: 110, px: 0.5, borderRadius: 1 }}>
            {label}
            {input}
            {help}
            {showItemPhotos && (
                <ItemPhotos
                    projectId={projectId}
                    itemId={uploadId}
                    label="Foto's bij dit punt"
                    allowMultiple
                    disabled={disabled}
                />
            )}
            <ValidationDisplay
                warning={warning}
                onAddToRegister={warning && onAddToRegister ? () => onAddToRegister(item, warning) : null}
                inRegister={inRegister}
            />
        </Box>
    );
}
